import React from 'react';
import PropTypes from 'prop-types';
import useRefinementList from 'hooks/useRefinementList';
import FacetSelect from './FacetSelect';

/**
 * Location filter that shows location.text values (e.g. "Manchester, United Kingdom",
 * "United Kingdom") as options. When the selected value exactly matches a known
 * location.country value, it refines on location.country so that all users in that
 * country are matched — not just those whose location.text is exactly that string.
 * City-level selections refine on location.text as normal.
 */
const LocationSelector = ({ search, placeholder }) => {
  const {
    items: textItems,
    refined: refinedText,
    refine: refineText,
  } = useRefinementList(search, 'location.text', {
    limit: 500,
    operator: 'or',
  });

  const {
    items: countryItems,
    refined: refinedCountry,
    refine: refineCountry,
  } = useRefinementList(search, 'location.country', { operator: 'or' });

  const countryValues = new Set(countryItems.map((i) => i.value));

  const handleSelect = (selected) => {
    if (!selected) return;
    if (countryValues.has(selected.value)) {
      refineCountry(selected.value);
    } else {
      refineText(selected.value);
    }
  };

  const handleRemovePill = (key) => {
    const [type, value] = key.split('::');
    if (type === 'country') {
      refineCountry(value);
    } else {
      refineText(value);
    }
  };

  // Exclude location.text entries already covered by a country refinement
  const selectOptions = textItems
    .filter((item) => !item.isRefined && !refinedCountry.includes(item.value))
    .map((item) => ({
      label: item.label,
      value: item.value,
      count: item.count,
    }))
    .sort((a, b) => b.count - a.count);

  const pills = [
    ...refinedCountry.map((v) => ({ key: `country::${v}`, label: v })),
    ...refinedText.map((v) => ({ key: `text::${v}`, label: v })),
  ];

  return (
    <FacetSelect
      options={selectOptions}
      pills={pills}
      placeholder={placeholder}
      onSelect={handleSelect}
      onRemovePill={handleRemovePill}
      requireSearch
    />
  );
};

LocationSelector.propTypes = {
  search: PropTypes.object,
  placeholder: PropTypes.string,
};

LocationSelector.defaultProps = {
  placeholder: 'Search city or country…',
};

export default LocationSelector;
